import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReloadOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Dayjs } from 'dayjs';
import { useSnapshot } from 'valtio';
import { getPetFieldDictListApi } from '@/api/petManage/petFieldDict';
import { getPetDetailApi, getPetListApi } from '@/api/petManage/petList';
import { getPetTagListNotApi, PetTagItem } from '@/api/petManage/petTag';
import { PET_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';

const { RangePicker } = DatePicker;

const petTypeOptions = [
  { label: '全部', value: '' },
  { label: '狗', value: 1 },
  { label: '猫', value: 2 },
];
const shopFlagOptions = [
  { label: '全部', value: '' },
  { label: '本店', value: 1 },
  { label: '非本店', value: 2 },
];

const diseaseFieldCode = 'medicalHistoryDetails';
const petTypeText = (value?: string | number) => {
  if (value === 1 || value === '1') return '狗';
  if (value === 2 || value === '2') return '猫';
  return '-';
};
const shopFlagText = (value?: string | number) => {
  if (value === 1 || value === '1') return '本店';
  if (value === 2 || value === '2') return '非本店';
  return '/';
};

const normalizeRange = (range?: Dayjs[]) => ({
  start: range?.[0] ? range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
  end: range?.[1] ? range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
});

const maskMobile = (mobile?: string) => {
  if (!mobile) return '-';
  if (mobile.length < 7) return mobile;
  return `${mobile.slice(0, 3)}****${mobile.slice(-4)}`;
};

const PetList: React.FC = () => {
  const { currentShopType, currentShopId } = useSnapshot(state);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [diseaseOptions, setDiseaseOptions] = useState<{ label: string; value: string }[]>([]);
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string | number }[]>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagPageNum, setTagPageNum] = useState(1);
  const [tagTotal, setTagTotal] = useState(0);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailDictMap, setDetailDictMap] = useState<Record<string, { label: string; value: string }[]>>({});
  const TAG_PAGE_SIZE = 10;
  const canViewDetail = useMemo(() => checkPermissions(PET_KEY.DETAIL), []);

  const formatDictOptions = (fieldCode: string, list: any[] = []) => {
    const seen = new Set<string>();
    return list
      .map((item: any) => ({ label: item.dictName, value: String(item.dictCode) }))
      .filter(opt => {
        if (fieldCode === 'dailyActivityMagnitude') {
          if (!['1', '2', '3'].includes(opt.value)) return false;
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        }
        return true;
      });
  };

  const fetchDetailDicts = useCallback(async (petType?: string | number) => {
    const targets = [
      { fieldCode: 'healthStatus', withPetType: true },
      { fieldCode: 'bodyShape', withPetType: false },
      { fieldCode: 'dailyActivityMagnitude', withPetType: true },
      { fieldCode: 'dailyActivityVenue', withPetType: false },
      { fieldCode: 'isPickyEating', withPetType: false },
      { fieldCode: 'medicalHistoryDetails', withPetType: false },
      { fieldCode: 'orderCycle', withPetType: false },
    ];
    const resultMap: Record<string, { label: string; value: string }[]> = {};
    await Promise.all(
      targets.map(async target => {
        try {
          const params: any = { fieldCode: target.fieldCode };
          if (target.withPetType && petType !== undefined && petType !== null) {
            params.petType = petType;
          }
          const res = await getPetFieldDictListApi(params);
          if (res?.code === 200) {
            resultMap[target.fieldCode] = formatDictOptions(target.fieldCode, res.data || []);
          } else {
            resultMap[target.fieldCode] = [];
          }
        } catch (error) {
          console.error('获取字典失败', target.fieldCode, error);
          resultMap[target.fieldCode] = [];
        }
      }),
    );
    setDetailDictMap(resultMap);
  }, []);

  const getDetailDictLabel = (fieldCode: string, value: any) => {
    const options = detailDictMap[fieldCode] || [];
    const match = options.find(opt => String(opt.value) === String(value));
    return match?.label || value || '-';
  };

  const fetchList = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    const values = form.getFieldsValue();
    const createRange = normalizeRange(values.createTime);
    const updateRange = normalizeRange(values.updateTime);
    const params = {
      petNickName: values.petNickName || undefined,
      mobile: values.mobile || undefined,
      medicalHistoryDetailsIds: Array.isArray(values.medicalHistoryDetailsIds)
        ? values.medicalHistoryDetailsIds.filter(Boolean).join(',')
        : undefined,
      petType: values.petType || undefined,
      shopFlag: values.shopFlag || undefined,
      tagId: values.tagId || undefined,
      oneId: values.oneId || undefined,
      createStartTime: createRange.start,
      createEndTime: createRange.end,
      updateStartTime: updateRange.start,
      updateEndTime: updateRange.end,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
      shopId: currentShopId,
    };
    setLoading(true);
    try {
      const res = await getPetListApi(params);
      if (res.code === 200) {
        setDataSource(res.rows || []);
        setTotal(res.total ?? 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDetailDicts]);

  useEffect(() => {
    const fetchDiseaseOptions = async () => {
      try {
        const res = await getPetFieldDictListApi({ fieldCode: diseaseFieldCode });
        if (res.code === 200) {
          setDiseaseOptions(
            (res.data || []).map((item: any) => ({
              label: item.dictName,
              value: String(item.dictCode ?? ''),
            })),
          );
        }
      } catch (error) {
        console.error('获取疾病类型失败', error);
      }
    };
    fetchDiseaseOptions();
  }, [fetchDetailDicts]);

  const fetchTags = async (pageNumParam = 1) => {
    setTagLoading(true);
    try {
      const res = await getPetTagListNotApi({ pageNum: pageNumParam, pageSize: TAG_PAGE_SIZE });
      const rows: PetTagItem[] = Array.isArray(res?.rows) ? res.rows : [];
      const total = typeof res?.total === 'number' ? res.total : 0;
      const options = rows.map((item: PetTagItem) => ({
        label: item.tagName || '',
        value: (item.id ?? '') as string | number,
      }));
      if (pageNumParam === 1) {
        setTagOptions(options);
      } else {
        setTagOptions(prev => [...prev, ...options]);
      }
      setTagTotal(total);
      setTagPageNum(pageNumParam);
    } catch (error) {
      console.error('获取宠物标签失败', error);
    } finally {
      setTagLoading(false);
    }
  };

  const handleViewDetail = useCallback(
    async (record: any) => {
      if (!record.petOneId) {
        message.warning('缺少宠物ID');
        return;
      }
      setDetailVisible(true);
      setDetailLoading(true);
      try {
        const res = await getPetDetailApi({ petOneId: record.petOneId });
        if (res.code === 200) {
          const detail = res.data || null;
          setDetailData(detail);
          if (detail?.petType !== undefined) {
            fetchDetailDicts(detail.petType);
          }
        } else {
          message.error(res.msg || '获取详情失败');
        }
      } catch (error) {
        console.error('获取详情异常', error);
        message.error('获取详情失败');
      } finally {
        setDetailLoading(false);
      }
    },
    [fetchDetailDicts],
  );

  const columns: ColumnsType<any> = useMemo(
    () => [
      { title: '宠物名', dataIndex: 'petNickName', width: 140, ellipsis: true },
      {
        title: '头像',
        dataIndex: 'petJdImg',
        width: 100,
        render: (_, record) => {
          const src = record.petJdImg || record.petTmImg;
          return (
            <Avatar
              src={src}
              icon={!src ? <UserOutlined /> : undefined}
            />
          );
        },
      },
      {
        title: '品种类型',
        dataIndex: 'petType',
        width: 120,
        render: value => petTypeText(value),
      },
      { title: '品种名称', dataIndex: 'petBreedName', width: 160, ellipsis: true },
      {
        title: '档案来源',
        dataIndex: 'shopFlag',
        width: 120,
        render: value => shopFlagText(value),
      },
      { title: '出生日期', dataIndex: 'birthday', width: 140 },
      {
        title: '会员手机号',
        dataIndex: 'mobile',
        width: 150,
        render: value => maskMobile(value),
      },
      {
        title: '会员平台ID',
        dataIndex: 'shopUserId',
        width: 180,
        ellipsis: true,
        render: (_, record) => record.shopUserId || record.userId || '-',
      },
      {
        title: '疾病类型',
        dataIndex: 'medicalHistoryDetails',
        width: 160,
        render: value => {
          const match = diseaseOptions.find(opt => String(opt.value) === String(value));
          return match?.label || value || '-';
        },
      },
      { title: '创建时间', dataIndex: 'createTime', width: 180 },
      { title: '更新时间', dataIndex: 'updateTime', width: 180 },
      {
        title: '操作',
        width: 120,
        fixed: 'right',
        render: (_, record) =>
          canViewDetail ? (
            <Button
              type="link"
              onClick={() => handleViewDetail(record)}>
              详情
            </Button>
          ) : null,
      },
    ],
    [handleViewDetail, diseaseOptions, canViewDetail],
  );

  const renderDetailField = (label: string, value: React.ReactNode) => (
    <Descriptions.Item label={label}>
      {value === undefined || value === null || value === '' ? (
        <Typography.Text type="secondary">-</Typography.Text>
      ) : (
        value
      )}
    </Descriptions.Item>
  );

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}
        preserve={false}>
        <Form.Item
          label="宠物名"
          name="petNickName">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="品种类型"
          name="petType">
          <Select
            placeholder="全部"
            style={{ width: 160 }}
            options={petTypeOptions}
            showSearch
            filterOption={(input, option) => {
              const label = option?.label ?? '';
              return String(label).toLowerCase().includes(input.toLowerCase());
            }}
          />
        </Form.Item>
        <Form.Item
          label="会员oneId"
          name="oneId">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="会员手机号"
          name="mobile">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="档案来源"
          name="shopFlag">
          <Select
            placeholder="全部"
            style={{ width: 160 }}
            options={shopFlagOptions}
          />
        </Form.Item>
        <Form.Item
          label="创建时间"
          name="createTime">
          <RangePicker style={{ width: 320 }} />
        </Form.Item>
        <Form.Item
          label="更新时间"
          name="updateTime">
          <RangePicker style={{ width: 320 }} />
        </Form.Item>
        <Form.Item
          label="疾病类型"
          name="medicalHistoryDetailsIds">
          <Select
            mode="multiple"
            placeholder="全部"
            style={{ width: 200 }}
            allowClear
            options={diseaseOptions}
            showSearch
            filterOption={(input, option) => {
              const label = option?.label ?? '';
              return String(label).toLowerCase().includes(input.toLowerCase());
            }}
          />
        </Form.Item>
        <Form.Item
          label="宠物标签"
          name="tagId">
          <Select
            placeholder="全部"
            style={{ width: 200 }}
            allowClear
            options={tagOptions}
            loading={tagLoading}
            showSearch
            filterOption={(input, option) => {
              const label = option?.label ?? '';
              return String(label).toLowerCase().includes(input.toLowerCase());
            }}
            onOpenChange={open => {
              if (open && !tagOptions.length) fetchTags(1);
            }}
            onPopupScroll={e => {
              const target = e.target as HTMLDivElement;
              if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
                const hasMore = tagOptions.length < tagTotal;
                if (!tagLoading && hasMore) {
                  fetchTags(tagPageNum + 1);
                }
              }
            }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                setPageNum(1);
                fetchList({ pageNum: 1 });
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey={record => record.petOneId || `${record.petNickName}-${record.mobile}`}
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, size) => fetchList({ pageNum: page, pageSize: size }),
        }}
        scroll={{ x: 1200 }}
      />
      <Drawer
        title="宠物详情"
        width={480}
        destroyOnHidden
        onClose={() => {
          setDetailVisible(false);
          setDetailData(null);
        }}
        open={detailVisible}>
        <Spin spinning={detailLoading}>
          {detailData ? (
            <div>
              <Space
                size={12}
                style={{ marginBottom: 16 }}>
                <Avatar
                  size={72}
                  src={detailData.petJdImg || detailData.petTmImg}
                  icon={<UserOutlined />}
                />
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{detailData.petNickName || '-'}</div>
                  <Typography.Text type="secondary">ID：{detailData.petOneId || '-'}</Typography.Text>
                  <div style={{ color: '#999', marginTop: 4 }}>
                    {detailData.ageYear ? `${detailData.ageYear}` : '--'} / {detailData.petLifeStageName || '-'}
                  </div>
                </div>
              </Space>
              <div style={{ marginBottom: 16 }}>
                {(detailData.tagList || []).length ? (
                  <Space
                    size={[8, 8]}
                    wrap>
                    {detailData.tagList.map((tag: any) => (
                      <Tag
                        key={tag?.code || tag?.id || tag?.name}
                        color="blue">
                        {tag}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">暂无标签</Typography.Text>
                )}
              </div>
              <Divider />
              <Descriptions
                title="宠物档案"
                column={1}
                size="small"
                labelStyle={{ width: 96 }}>
                {renderDetailField('宠物分类', petTypeText(detailData.petType))}
                {renderDetailField('宠物昵称', detailData.petNickName || '-')}
                {renderDetailField('宠物品种', detailData.petBreedName || '-')}
                {renderDetailField('宠物生日', detailData.birthday || '-')}
              </Descriptions>
              <Divider />
              <Descriptions
                title="健康评估信息"
                column={1}
                size="small"
                labelStyle={{ width: 96 }}>
                {renderDetailField('爱宠性别', detailData.gender === 1 ? '公' : detailData.gender === 2 ? '母' : '-')}
                {renderDetailField(
                  '是否绝育',
                  detailData.isNeutered === 1 ? '是' : detailData.isNeutered === 0 ? '否' : '-',
                )}
                {renderDetailField('体重', detailData.weight ? `${detailData.weight}kg` : '-')}
                {Number(currentShopType) !== 1 &&
                  renderDetailField('健康状态', getDetailDictLabel('healthStatus', detailData.healthStatus))}
                {renderDetailField('体型状态', getDetailDictLabel('bodyShape', detailData.bodyShape))}
                {renderDetailField(
                  '活跃度',
                  getDetailDictLabel(
                    'dailyActivityMagnitude',
                    detailData.dailyActivityMagnitude ?? detailData.dailyActivityLevel,
                  ),
                )}
                {renderDetailField('活动范围', getDetailDictLabel('dailyActivityVenue', detailData.dailyActivityVenue))}
                {renderDetailField('挑食情况', getDetailDictLabel('isPickyEating', detailData.isPickyEating))}
                {renderDetailField(
                  '疾病',
                  getDetailDictLabel('medicalHistoryDetails', detailData.medicalHistoryDetails),
                )}
                {renderDetailField('下单周期', getDetailDictLabel('orderCycle', detailData.orderCycle))}
              </Descriptions>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无数据"
            />
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default PetList;
